import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { useMessage } from "../../../../base/MessageProvider";
import { Button, SvgIcon, TextField } from "@mui/material";
import { ReactComponent as Sparkle } from "../../../../assets/icons/sparkle.svg";
import { ReactComponent as MagicStick } from "../../../../assets/icons/magic-stick.svg";
import {
  setAutofillDescription,
  setFormRoleType,
} from "../../../actions/project";
import { setSelectedRecruiter } from "../../../actions/recruiter";
import useMediaQuery from "../../../../base/useMediaQuery";

const AutoFillAICard = ({ setAiModalOpen, onSubmit }) => {
  const alert = useMessage();
  const dispatch = useDispatch();

  const autofillDescription = useSelector(
    (state) => state.projectReducer.autofillDescription
  );

  const isMobile = useMediaQuery("(max-width:600px)");
  const isTablet = useMediaQuery("(max-width:900px)");

  // Validation schema
  const validationSchema = Yup.object().shape({
    description: Yup.string(),
  });

  // Form control setup
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      description: "",
    },
  });

  useEffect(() => {
    if (autofillDescription) {
      setValue("description", autofillDescription);
    }
  }, [autofillDescription]);

  const description = watch("description");

  const handleAIAutofill = useCallback(async () => {
    try {
      dispatch(setFormRoleType("add"));
    } catch (error) {
      console.error(error);
    }
  }, [alert, dispatch]);

  const handleSavePress = useCallback(() => {
    if (!description.trim()) {
      alert.showError("Please insert a few lines to try Autofill.", {
        duration: 5000,
      });
      return;
    }

    dispatch(setAutofillDescription(description));
    dispatch(setSelectedRecruiter(null));

    handleAIAutofill();
    setAiModalOpen(false);
    onSubmit();
  }, [description, handleAIAutofill, setAiModalOpen, onSubmit]);

  const containerRef = useRef(null);
  const [minRows, setMinRows] = useState(16); // Default value

  useEffect(() => {
    if (containerRef.current) {
      const height = containerRef.current.clientHeight; // Get outer div height
      const calculatedRows = Math.floor(height / 24); // Approximate row height
      setMinRows(calculatedRows);
    }
  }, []);

  return (
    <>
      <div className="flex items-center gap-2 mb-4" style={{ height: "8%" }}>
        <SvgIcon component={Sparkle} />
        <h3 className="text-md text-white">
          Could you describe your project in detail?
        </h3>
      </div>
      <div
        ref={containerRef}
        className="bg-[#252525]"
        style={{ height: "80%" }}
      >
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              placeholder="Describe Project"
              fullWidth
              multiline
              minRows={minRows} // Dynamically set based on div height
              maxRows={minRows} // Allow some expansion
              className="bg-[#252525] text-white border border-[#E7EBF1] rounded-lg"
              InputProps={{
                sx: {
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#252525" },
                    "&:hover fieldset": { borderColor: "#252525" },
                    "&.Mui-focused fieldset": { borderColor: "#252525" },
                    color: "white",
                    caretColor: "white",
                  },
                  "& .MuiInputBase-input": {
                    color: "white",
                    scrollbarWidth: "thin", // Makes the scrollbar thinner
                    scrollbarColor: "#444 #252525", // Dark scrollbar with darker background
                    "&::-webkit-scrollbar": {
                      width: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "#252525",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#444",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      background: "#555",
                    },
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "white",
                    opacity: 1,
                  },
                },
              }}
            />
          )}
        />
      </div>
      <div className="flex text-end" style={{ height: "12%" }}>
        <Button
          variant="contained"
          className="mt-4 mb-4  bg-white text-black px-4 py-2 rounded-lg w-full items-center flex"
          onClick={handleSavePress}
        >
          <SvgIcon component={MagicStick} />
          Try Autofill with AI
        </Button>
      </div>
    </>
  );
};

export default AutoFillAICard;
